import { PackageCheck, Truck, Warehouse } from "lucide-react";
import { cx, Tip } from "@/components/ui";
import type { OrderDetail } from "@/lib/api";
import { t } from "@/lib/i18n";

/**
 * Stand je Position nach der Übergabe ans Lager: Ab da hält das Lager die Ware fest, die Bestandsvergabe des Portals
 * greift nicht mehr. Gegen Überladung: Haben alle Positionen denselben Stand, steht er einmal in der Kopfzeile –
 * je Position nur, wenn sie sich unterscheiden (Teilversand) oder mehrere Pakete unterwegs sind.
 */
export function shipmentView(o: OrderDetail) {
  if (o.importedFrom || !["submitted", "in_progress", "partially_shipped", "shipped"].includes(o.status)) return null;
  const lines = o.lines.filter((l) => !l.addedByRule);
  const shipped = new Map<string, number>();
  const parcels = new Map<string, string[]>();
  for (const s of o.shipments) {
    for (const l of s.lines) {
      shipped.set(l.sku, (shipped.get(l.sku) ?? 0) + l.quantity);
      parcels.set(l.sku, [...(parcels.get(l.sku) ?? []), ...s.trackingNumbers]);
    }
  }
  // komplett versendet gilt auch ohne Inhaltsangaben (z. B. Sets, die das Lager in Bestandteilen meldet)
  const all = o.status === "shipped";
  const per = lines.map((l) => ({ id: l.id, quantity: l.quantity, shipped: all ? l.quantity : Math.min(l.quantity, shipped.get(l.sku) ?? 0), parcels: [...new Set(parcels.get(l.sku) ?? [])] }));
  const done = per.filter((p) => p.shipped >= p.quantity).length;
  const multiParcel = new Set(o.shipments.flatMap((s) => s.trackingNumbers)).size > 1;
  return { per, done, total: per.length, perLine: (done > 0 && done < per.length) || per.some((p) => p.shipped > 0 && p.shipped < p.quantity) || (all && multiParcel) };
}

/** Kopfzeile der Positionen nach der Übergabe */
export function ShipmentSummary({ o }: { o: OrderDetail }) {
  const v = shipmentView(o);
  if (!v || o.warehouseParkedAt) return null;
  const chip = (tone: "success" | "info", icon: React.ReactNode, text: string) => (
    <span className={cx("ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal", tone === "success" ? "bg-success-soft text-success" : "bg-info-soft text-info")}>
      {icon}
      {text}
    </span>
  );
  if (v.done === v.total) return chip("success", <Truck className="size-3.5" />, t("Alle Positionen versendet"));
  if (v.done > 0 || v.per.some((p) => p.shipped > 0)) return chip("info", <Truck className="size-3.5" />, t("{d} von {n} Positionen versendet", { d: v.done, n: v.total }));
  if (o.status === "partially_shipped") return chip("info", <Truck className="size-3.5" />, t("Teilweise versendet – Sendungsdaten folgen"));
  return o.status === "in_progress" ? chip("info", <Warehouse className="size-3.5" />, t("Wird kommissioniert")) : chip("success", <PackageCheck className="size-3.5" />, t("Im Lager reserviert"));
}

const short = (tn: string) => (tn.length > 8 ? `…${tn.slice(-6)}` : tn);

/** je Position (nur wenn sich die Positionen unterscheiden): versendet mit Paket, teilweise, noch im Lager */
export function LineShipment({ o, lineId }: { o: OrderDetail; lineId: string }) {
  const v = shipmentView(o);
  if (!v?.perLine) return null;
  const p = v.per.find((x) => x.id === lineId);
  if (!p) return null;
  const parcels = p.parcels.length ? (
    <Tip content={p.parcels.join(", ")}>
      <span className="text-fg-subtle tabular-nums cursor-help">{t("Paket")} {p.parcels.map(short).join(", ")}</span>
    </Tip>
  ) : null;
  if (p.shipped >= p.quantity)
    return (
      <div className="mt-1 flex items-center gap-2 text-[11.5px]">
        <span className="inline-flex items-center gap-1 font-medium text-success"><Truck className="size-3.5" />{t("Versendet")}</span>
        {parcels}
      </div>
    );
  if (p.shipped > 0)
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px]">
        <span className="relative inline-flex h-1.5 w-14 overflow-hidden rounded-full bg-info/25" aria-hidden><span className="h-full bg-success" style={{ width: `${(p.shipped / p.quantity) * 100}%` }} /></span>
        <span className="font-medium text-info">{t("{s} von {n} versendet", { s: p.shipped, n: p.quantity })}</span>
        {parcels}
        <span className="text-fg-muted">{t("Rest noch im Lager")}</span>
      </div>
    );
  return (
    <div className="mt-1 flex items-center gap-2 text-[11.5px]">
      <span className="inline-flex items-center gap-1 font-medium text-info"><Warehouse className="size-3.5" />{t("Noch im Lager")}</span>
    </div>
  );
}
